import React, { useState, useEffect } from "react";
import { Card, CardBody, Row, Col, Button, Spinner, Container, Badge, Input, InputGroup, Modal, Table } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn, H3 } from "../../AbstractElements";
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import { Edit, Plus, Trash2, Search, Eye, Shield } from 'react-feather';
import { useAuth } from "../../Context/AuthContext";
interface RoleData {
    id: number;
    name: string;
    description: string;
    permissions_count: number;
    permissions?: Array<{ id: number; module: string; action: string; slug: string; section?: string; }>;
}

export default function ViewRoles() {
    const [data, setData] = useState<RoleData[]>([]);
    const [filteredData, setFilteredData] = useState<RoleData[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [refresh, setRefresh] = useState(0);
    const [viewModal, setViewModal] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [selectedRole, setSelectedRole] = useState<RoleData | null>(null);
    const navigate = useNavigate();
    const { hasPermission } = useAuth();

    useEffect(() => {
        fetchData();
    }, [refresh]);

    useEffect(() => {
        const filtered = data.filter(item =>
            item.name.toLowerCase().includes(searchText.toLowerCase()) ||
            (item.description && item.description.toLowerCase().includes(searchText.toLowerCase()))
        );
        setFilteredData(filtered);
    }, [searchText, data]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response: any = await apiPost("role/get_all", {});
            const roles = Array.isArray(response) ? response : response.data || [];
            setData(roles);
            setFilteredData(roles);
        } catch (error) {
            toast.error("Failed to load roles");
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => navigate(`${process.env.PUBLIC_URL}/add_role`);
    const handleEdit = (id: number) => navigate(`${process.env.PUBLIC_URL}/edit_role`, { state: { id } });

    const handleView = (role: RoleData) => {
        setSelectedRole(role);
        setViewModal(true);
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost(`role/delete`, { id });
                    toast.success("Role deleted successfully");
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error("Failed to delete role");
                }
            }
        });
    };

    const columns = [
        {
            name: <div><span className="fw-bold">Sl.No</span></div>,
            selector: (row: any, index: any) => index + 1,
            width: "70px",
            sortable: false,
        },
        {
            name: <div><span className="fw-bold">Role Name</span></div>,
            selector: (row: RoleData) => row.name,
            sortable: true,
            minWidth: "120px",
            cell: (row: RoleData) => <span className="fw-bold text-primary">{row.name}</span>
        },
        {
            name: <div><span className="fw-bold">Permissions Count</span></div>,
            selector: (row: RoleData) => row.permissions_count,
            sortable: true,
            center: true,
            minWidth: "160px",
            cell: (row: RoleData) => <Badge color="info" pill style={{ fontSize: '13px', padding: '6px 12px' }}>{row.permissions_count}</Badge>
        },
        {
            name: <div><span className="fw-bold">Actions</span></div>,
            width: "150px",
            center: true,
            cell: (row: RoleData) => (
                <div className="d-flex gap-2 justify-content-center">
                    <Button size="sm" color="primary" onClick={() => handleView(row)} title="View Permissions" className="p-1 px-2">
                        <Eye size={14} />
                    </Button>
                    {hasPermission('role-management.edit') && (
                        <Button size="sm" color="info" className="text-white p-1 px-2" onClick={() => handleEdit(row.id)} title="Edit Role">
                            <Edit size={14} />
                        </Button>
                    )}
                    {hasPermission('role-management.delete') && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete Role" className="p-1 px-2">
                            <Trash2 size={14} />
                        </Button>
                    )}
                </div>
            ),
        },
    ];



    // Group permissions by section and then by module for the view modal
    const permissionsBySection = selectedRole?.permissions?.reduce((acc: any, curr: any) => {
        const sectionName = curr.section || 'OTHER';
        if (!acc[sectionName]) acc[sectionName] = {};
        if (!acc[sectionName][curr.module]) acc[sectionName][curr.module] = [];
        acc[sectionName][curr.module].push(curr.action);
        return acc;
    }, {}) || {};

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: { fontSize: '15px', fontWeight: '600', paddingLeft: '16px', paddingRight: '16px' },
        },
        rows: {
            style: { minHeight: '50px' },
        },
    };


    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="Role Management" parent="Master Settings" title="Role Management" />
            <Container fluid>
                <Card className="shadow-sm">
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <H3 className="text-primary mb-0">Roles Overview</H3>
                            {hasPermission('role-management.add') && (
                                <Btn color="primary" onClick={handleAdd}>
                                    <Plus size={16} className="me-1" /> Add Role
                                </Btn>
                            )}
                        </div>

                        <Row className="mb-3 justify-content-end">
                            <Col md={4} lg={3}>
                                <InputGroup className="shadow-sm border-0">
                                    <Input
                                        placeholder="Search..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        className="form-control-sm"
                                    />
                                </InputGroup>
                            </Col>
                        </Row>

                        <div className="table-responsive bg-white rounded">
                            <DataTable
                                columns={columns}
                                data={filteredData}
                                progressPending={loading}
                                progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                pagination
                                paginationPerPage={10}
                                paginationRowsPerPageOptions={[10, 25, 50]}
                                highlightOnHover
                                pointerOnHover
                                customStyles={customStyles}
                                noDataComponent={
                                    <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "50vh" }}>
                                        <Card className="shadow-sm border-0 text-center" style={{ maxWidth: "320px", width: "100%" }}>
                                            <CardBody>
                                                <h6 className="fw-semibold mb-1">
                                                    No Roles Found
                                                </h6>
                                                <small className="text-muted">
                                                    You haven't created any roles yet.
                                                </small>
                                            </CardBody>
                                        </Card>
                                    </div>
                                }
                            />
                        </div>
                    </CardBody>
                </Card>
            </Container>

            {/* View Permissions Modal */}
            <Modal isOpen={viewModal} toggle={() => setViewModal(!viewModal)} size="lg" centered>
                {selectedRole && (
                    <div style={{
                        background: 'linear-gradient(135deg, #1e6ec8 0%, #0d4fa0 100%)',
                        padding: '1.5rem',
                        position: 'relative',
                        width: '100%'
                    }}>
                        <button
                            className="btn-close btn-close-white"
                            type="button"
                            onClick={() => setViewModal(false)}
                            style={{ position: 'absolute', top: '1rem', right: '1rem', opacity: 0.9 }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                            <div style={{
                                width: '60px', height: '60px', borderRadius: '50%',
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0
                            }}>
                                <Shield size={28} color="white" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3 style={{ color: 'white', fontWeight: 700, fontSize: '1.4rem', margin: 0, marginBottom: '0.25rem' }}>
                                    {selectedRole.name}
                                </h3>
                                <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
                                    Active Role • <strong>{selectedRole.permissions_count}</strong> Permissions
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div className="modal-body p-0">
                    <div className="p-3 bg-light border-bottom">
                        <Btn color="primary" size="sm" className="d-flex align-items-center gap-2">
                            <i className="fa fa-lock"></i> Permissions Matrix
                        </Btn>
                    </div>
                    <div className="table-responsive" style={{ maxHeight: '60vh' }}>
                        <Table className="mb-0 align-middle">
                            <thead className="table-light sticky-top">
                                <tr>
                                    <th style={{ width: '30%' }}>Module</th>
                                    <th>Permissions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.keys(permissionsBySection).length > 0 ? (
                                    <>
                                        {/* Sort sections to keep GENERAL at top if it exists */}
                                        {Object.keys(permissionsBySection).sort((a, b) => {
                                            if (a === 'GENERAL') return -1;
                                            if (b === 'GENERAL') return 1;
                                            return a.localeCompare(b);
                                        }).map(section => (
                                            <React.Fragment key={section}>
                                                <tr className="bg-light">
                                                    <td colSpan={2} className="fw-bold py-1 text-primary small ps-3 border-top">
                                                        {section.toUpperCase()}
                                                    </td>
                                                </tr>
                                                {Object.keys(permissionsBySection[section]).map(module => (
                                                    <tr key={module}>
                                                        <td className="fw-bold ps-4">{module}</td>
                                                        <td>
                                                            <div className="d-flex flex-wrap gap-2">
                                                                {permissionsBySection[section][module].map((action: string) => (
                                                                    <Badge key={action} color="success" className="d-flex align-items-center gap-1" pill style={{ fontSize: '11px' }}>
                                                                        <i className="fa fa-check-circle" style={{ fontSize: '10px' }}></i> {action}
                                                                    </Badge>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </>
                                ) : (
                                    <tr>
                                        <td colSpan={2} className="text-center py-4 text-muted">No permissions assigned to this role</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>

                    </div>
                </div>
            </Modal>

            <ToastContainer />
        </div>
    );
}
